import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, IconButton, Menu, MenuItem, Typography, } from '@mui/material';
import React from 'react';
import { CardGrid } from '../../CardGrid';
import { DraggableCard } from '../../Cards/DraggableCard';
import { themeColors } from '../../JuvareTheme';
import { useCommonWebTranslation } from '../../hooks';
/**
 * Provides a dropdown menu for managing tabs in the global tabs bar.
 * Allows users to open tabs, reorder them in a dialog, or close all tabs.
 *
 * @param {GlobalTabsMenuProps} props - Component props.
 * @param {GlobalTab[]} props.tabs - List of currently opened tabs.
 * @param {(tab: GlobalTab) => void} props.onOpenTab - Callback when a tab is selected to be opened.
 * @param {(tabs: GlobalTab[]) => void} [props.onChangeTabs] - Optional callback to update the order of tabs.
 * @param {() => void} props.onCloseAllTabs - Callback to close all tabs at once.
 * @returns {JSX.Element} The rendered component.
 */
export const GlobalTabsMenu = (props) => {
    const { t } = useCommonWebTranslation();
    const [anchorElement, setAnchorElement] = React.useState();
    const [showReorderTabsDialog, setShowReorderTabsDialog] = React.useState(false);
    const handleOpenMenu = (event) => {
        setAnchorElement(event.currentTarget);
    };
    const handleCloseMenu = () => {
        setAnchorElement(undefined);
    };
    const handleOpenTab = (tab) => {
        setAnchorElement(undefined);
        props.onOpenTab(tab);
    };
    const handleReorderTabs = () => {
        setAnchorElement(undefined);
        setShowReorderTabsDialog(true);
    };
    const handleCloseAllTabs = () => {
        setAnchorElement(undefined);
        props.onCloseAllTabs();
    };
    return (React.createElement(React.Fragment, null,
        React.createElement(IconButton, { sx: { color: themeColors.text.link }, onClick: handleOpenMenu, size: 'medium', "aria-label": t('common.menu'), "data-test": 'tabs-menu-button' }, React.createElement(ManageSearchIcon, null)),
        React.createElement(Menu, { anchorEl: anchorElement, open: !!anchorElement, onClose: handleCloseMenu, anchorOrigin: { horizontal: 'right', vertical: 'top' }, transformOrigin: { horizontal: 'right', vertical: 'top' }, sx: {
                width: {
                    xm: '100%',
                    sm: 375,
                    md: 375,
                    lg: 375,
                    xl: 375,
                },
            }, "data-test": 'tabs-menu' },
            props.tabs.map((tab) => tab.href ? (React.createElement(MenuItem, { key: tab.path, component: 'a', href: tab.href, onClick: (event) => {
                    event.preventDefault();
                    handleOpenTab(tab);
                }, onKeyDown: (event) => {
                    var _a;
                    const target = event.target;
                    if (!event.shiftKey && event.key === 'Tab' && target.nodeName === 'A') {
                        event.stopPropagation();
                        event.preventDefault();
                        (_a = target.querySelector('button')) === null || _a === void 0 ? void 0 : _a.focus();
                    }
                }, sx: {
                    gap: 2,
                }, "data-test": tab['data-test'], title: tab.label },
                React.createElement(Typography, { variant: 'inherit', noWrap: true, sx: { flexGrow: 1, py: 1 } }, tab.label),
                React.createElement(IconButton, { onMouseDown: (event) => {
                        event.stopPropagation();
                    }, onClick: (event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        window.open(tab.href, '_blank');
                        if (!event.ctrlKey && !event.metaKey) {
                            handleCloseMenu();
                        }
                    }, "aria-label": t('globalTabsBar.openInNewBrowserTab') },
                    React.createElement(OpenInNewIcon, { fontSize: 'small', sx: { color: themeColors.text.link } })))) : (React.createElement(MenuItem, { key: tab.path, onClick: () => handleOpenTab(tab), "data-test": tab['data-test'], title: tab.label },
                React.createElement(Typography, { variant: 'inherit', noWrap: true, sx: { flexGrow: 1, py: 1 } }, tab.label)))),
            props.tabs.length > 1 &&
                props.onChangeTabs && [
                React.createElement(Divider, { key: 'divider' }),
                React.createElement(MenuItem, { key: 'reorder-tabs-menuItem', onClick: handleReorderTabs, sx: { display: 'flex', justifyContent: 'center' }, "data-test": 'reorder-tabs' },
                    React.createElement(Typography, { variant: 'button', fontSize: 'medium', sx: { display: 'flex', alignItems: 'center', gap: 1, color: themeColors.text.link } }, t('globalTabsBar.reorderTabs'))),
            ],
            React.createElement(Divider, null),
            React.createElement(MenuItem, { onClick: handleCloseAllTabs, sx: { display: 'flex', justifyContent: 'center' }, "data-test": 'close-all-tabs' },
                React.createElement(Typography, { variant: 'button', fontSize: 'medium', sx: { display: 'flex', alignItems: 'center', gap: 1, color: themeColors.text.link } },
                    t('globalTabsBar.closeAllTabs'),
                    React.createElement(CancelOutlinedIcon, null)))),
        React.createElement(Dialog, { open: showReorderTabsDialog, onClose: () => setShowReorderTabsDialog(false), "data-test": 'tabs-reorder-dialog', fullWidth: true, maxWidth: 'xs' },
            React.createElement(DialogTitle, null, t('globalTabsBar.reorderTabs')),
            React.createElement(DialogContent, null,
                React.createElement(CardGrid, { cards: props.tabs.map((tab) => ({
                        id: tab.path,
                        tab: tab,
                    })), renderCard: (cardInGridProps, card) => (React.createElement(DraggableCard, Object.assign({}, cardInGridProps),
                        React.createElement(Box, { sx: { display: 'flex' } },
                            card.tab.icon && (React.createElement(Box, { sx: {
                                    display: 'flex',
                                    color: themeColors.text.link,
                                    pl: 2,
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }, "data-test": 'icon' }, card.tab.icon)),
                            React.createElement(Typography, { p: 2, sx: { overflow: 'hidden', textOverflow: 'ellipsis', fontSize: 14, p: 1 }, "data-test": 'title' }, card.tab.label)))), onChange: (cards) => { var _a; return (_a = props.onChangeTabs) === null || _a === void 0 ? void 0 : _a.call(props, cards.map((card) => card.tab)); }, editable: true, columns: 1, sx: { width: '100%', gridTemplateColumns: '100%', gap: 1, pt: 1, pb: 1 } })),
            React.createElement(DialogActions, null,
                React.createElement(Button, { onClick: () => setShowReorderTabsDialog(false) }, t('common.close'))))));
};
